
<template>
  <div>
    <div class="main-container is-desktop columns">

      <div class="no-pd-right-desktop pb-0 column-no-pad-mobile"
            :class="{'is-two-thirds-desktop': !narrowVideoMode, 'is-6': narrowVideoMode}"
        >
        <div class="box mb-0 pb-2 br-bottom-0"> <!-- video and tarp wrapper section -->
          <section>

            <VideoPlayer
              ref="video"
              v-if="video && video.processed"
              :src="video.url"
              @currentTime = "updateCurrentTime($event)"
              @duration = "duration = $event"
              :comments="video.comments"
              :narrowVideoMode="narrowVideoMode"
            />

            <div v-else-if="!video.processed && !video.transcode_error" class="player-height video-placeholder-container ">
              <p class="title is-4">Video successfully uploaded!</p>
              <p class="title is-2">Video Processing (Note this can sometimes take a while)</p>
              <Loader />
            </div>
            <div v-else-if="!video.processed && video.transcode_error" class="player-height video-placeholder-container ">
              <p class="title is-4">There was an error converting your video</p>
              <p class="title is-2">please delete this video and try again</p>
              <input
              @click="showDeleteVideoModal = true"
              v-if="isOwner && (video.processed || video.transcode_error)"
              type='button'
              class='button is-danger'
              value='delete'
              name='delete'/>

            </div>

            <div v-else-if="video == null" class="video-placeholder-container">
              <p class="title is-4">Sorry, we couldn't find this video. It either doesn't exist, or it hasn't been shared with you.</p>
            </div>
          </section>
          <div class="tarp pt-2"> <!-- tarp under video section -->
            <div v-if="editing" class="mb-4">
              <vue-slider
                :value="slider.value"
                :min="slider.min"
                :max="slider.max"
                :disabled="slider.disabled"
                :show="slider.show"
                :formatter="clipperTimeStampFormatter"
                @change="handleSliderChange"
                />

              <div class="flex-space-between flex-align-center start-end-container">
                <section class="flex-column">
                  <span class="is-bold">Start</span>
                  <span>{{ sliderStartTime | prettyCommentTime }}</span>
                </section>
                <section class="flex-column">
                  <span class="is-bold">Trimmed Length</span>
                  <span>{{ sliderEndTime - sliderStartTime | prettyCommentTime }}</span>
                </section>
                <section class="flex-column flex-end">
                  <span class="is-bold">End</span>
                  <span>{{ sliderEndTime | prettyCommentTime }}</span>
                </section>
              </div>

              <div class="flex-space-between flex-align-center">

                <button
                  @click="clipVideo({ replace: true })"
                  :disabled="!trimmable"
                  class='button is-warning white-btn-text'
                  :data-balloon="trimmable ? 'Cut the size of video' : 'This video is not trimmable because it isn\'t the original.'"
                  data-balloon-pos="right"
                  :class="clippingVideo && 'is-loading'">
                  Trim Original Video
                </button>

                <a :href="video.url" download>
                  <button
                    v-if="downloadable"
                    class='button is-info white-btn-text'
                    >
                    Download Video
                  </button>
                </a>

                <button
                  @click="clipVideo({ replace: false })"
                  class='button is-info'
                  :disabled="!clippable"
                  :data-balloon="!clippable && 'Modify video length to enable.'"
                  data-balloon-pos="left"
                  :class="clippingVideo && 'is-loading'">
                  Make New Clip
                </button>
              </div>
            </div>

            <section class="flex-space-between align-items-center flex-nowrap"
              :class="{ 'mb-4' : editing, 'bottom-border' : !editing}" >

              <h2 v-if="!editing" class="video-title mb-0" :title="video.title">
                <i>{{ video.title }}</i>
              </h2>

              <div v-if="editing" class="flex-grow">
                <input
                  type="text"
                  :value="video.title"
                  class="input is-medium"
                  placeholder="title"
                  ref="title" />
              </div>


              <div v-if="!video.transcode_error" class="editing-container">
                <div v-if="!editing && !isOwner" class="flex-grow">
                  <input
                    @click="showRemoveVideoModal = true"
                    type='button'
                    class='button is-link'
                    value='remove'
                    name='remove'/>
                </div>

                <a :href="video.url" download>
                  <button
                    v-if="!editing && ((isGroupAdmin && !isOwner) || downloadOverride)"
                    class='button is-info white-btn-text'
                    >
                    Download Video
                  </button>
                </a>

                <input
                  v-if="!editing && isOwner"
                  @click="editing = !editing"
                  type='button'
                  class='button is-link'
                  value='edit'
                  name='edit'/>

                <input
                  v-if="!editing && (isOwner || isCoachWhoCanShare)"
                  @click="toggleShowShareModal()"
                  type='button'
                  class='button is-info'
                  value='share'
                  name='share' />
              </div>
            </section>

            <section v-if="editing" class="mb-4">
              <textarea
                v-textautosize
                type="text"
                class="textarea"
                placeholder="Add a description..."
                v-model="description" />
            </section>

            <section v-else>

              <span class="d-flex align-items-center mt-1 mb-4">
                <hr>
                <img
                  class="user-profile-image m-0 mr-3"
                  :src="video.user.avatar_url" />
                <div>
                  <div><strong>{{ video.user.name || video.user.email }}</strong></div>
                  <div><em>{{ video.created_on | formatTextDate }}</em></div>
                </div>
              </span>

              <h2 class="subtitle line-breaks" v-if="video.description && !video.transcode_error">
                {{ video.description }}
              </h2>
              <p v-else-if="!video.transcode_error" class="text-muted">
                No video description. <span v-if="isOwner">Select <span class="bold">edit</span> to add one.</span>
              </p>
              <div
                v-if="!editing && video && video.folder && currentFolder != null && currentFolder.title"
                class="d-flex align-items-center text-muted">

                <FolderIcon/>
                <span
                  :data-balloon="currentFolder != null && currentFolder.description != null && currentFolder.description !== 'blank' ? `description: ${currentFolder.description}` : 'no description'"
                  data-balloon-pos="down">
                  {{ currentFolder.title }}
                </span>
              </div>
              <span v-for="tag in video.tags" class="tag is-primary">{{ tag.text }}</span>
            </section>

            <section class="d-flex align-items-center mb-2 flex-wrap" v-if="editing">
              <div class="d-flex align-items-center">
                <span class="mr-2 ml-2 text-bold bold">Comment Visibility</span>
                <span class="select">
                  <div class="select">
                    <select @change="updateCommenting($event, videoID)">
                      <option :selected="video.commenting === 'S'" value='S'>Standard</option>
                      <option :selected="video.commenting === 'P'" value='P'>Private</option>
                      <option :selected="video.commenting === 'D'" value='D'>Disabled</option>
                    </select>
                  </div>
                </span>
              <Dropdown class="ml-2" :title="'?'">
                <ul>
                  <li class="mb-2"><strong>Standard</strong> Everyone can view comments</li>
                  <li class="mb-2"><strong>Private</strong> Users only view their comments and the video owner sees all comments</li>
                  <li class="mb-1"><strong>Disabled</strong> No comments are visible</li>
                </ul>
              </Dropdown>
              </div>
            </section>

            <section class="d-flex align-items-center" v-if="editing">
              <div class="d-flex align-items-center flex-wrap">
                <div class="d-flex align-items-center">
                  <span class="mr-2 ml-2 bold">Current Folder</span>
                  <a
                    v-if="currentFolder && currentFolder.title"
                    :href="currentFolder.id && `/folders/${currentFolder.id}`"
                    class="mr-2 tag is-medium"
                    :data-balloon="currentFolder.description != null && currentFolder.description !== 'blank' ? `description: ${currentFolder.description}` : null"
                    data-balloon-pos="down">
                    {{ currentFolder.title }}
                  </a>
                  <span v-else class="mr-2 text-muted">none</span>
                  <button
                    @click="showMoveToFolderModal = true"
                    class="button mr-4 is-link">{{ video.folder != null ? 'Change Folder' : 'Move to Folder'}}
                  </button>
                </div>
              </div>
            </section>

            <section class="d-flex align-items-center" v-if="editing">
              <div class="flex-grow">
                <input
                  @click="showDeleteVideoModal = true"
                  v-if="isOwner && (video.processed || video.transcode_error)"
                  type='button'
                  class='button is-link'
                  value='delete'
                  name='delete'/>
              </div>

              <div>
                <input
                  v-if="isOwner"
                  @click="editing = false"
                  type='button'
                  class='button is-link '
                  value='cancel'
                  name='cancel'/>

                <input
                  @click="updateVideo(videoID)"
                  v-if="isOwner"
                  type='button'
                  class='button is-success '
                  value='save'
                  name='save'/>
              </div>
            </section>
          </div> <!-- end, tarp under video section -->
        </div> <!-- end, video and tarp wrapper section -->

        <div v-if="!video.transcode_error && !narrowVideoMode" class="box background-light br-top-0 mb-0">
          <div class="flex-space-between">
            <h2 class="is-size-4 fw-500">Video Resources</h2>
            <input
            @click="toggleShowAddFileModal()"
            :disabled="video == null"
            type='button'
            class='button'
            value='Attach Resource'
            name='Attach Resource'/>
          </div>

          <ul v-if="resources != null && resources.length > 0">
            <Resource v-for="resource in resources"
            :key="resource.id"
            :id="resource.id"
            :url="resource.url"
            :title="resource.title"
            :videoID="videoID"
            />
          </ul>
          <p v-if="resources != null && resources.length === 0" class="text-muted">No attached resources. Select the <span class="bold">Attach Resource</span class="text-bold"> button to add some.</p>
        </div>
        <div>
          <button v-if="hasObservationsPerm" class="button is-warning mt-2" @click="narrowVideoMode = !narrowVideoMode">{{ obsButtonLabel }}</button>
        </div>
      </div>

      <div v-if="!video.transcode_error && !narrowVideoMode" class='column-no-pad-mobile'>
        <!-- :class="{'column-no-pad-mobile': !narrowVideoMode }"> -->
        <h3 v-if="video.commenting == 'D' " class="title is-4 box">Comments are disabled</h3>
        <h3 v-if="video.commenting == 'P' " class="title is-4 box">Comments are set to private. You will only see your own.</h3>
        <Comments
          v-if="video.commenting != 'D' && !narrowVideoMode"
          :videoID = "videoID"
          :currentTime = "currentTime"
          @pause = "handlePause()"
          :video="video"
          :comments="video.comments"/>
      </div>
      <div v-else class='column is-6 pl-2'>
        <ArtsFormComments
          :videoID="videoID"
          :currentTime = "currentTime"/>
      </div>
    </div>

    <AddFileModal
      :videoID="videoID"
      v-if="showAddFileModal"
    />

    <Confirm
      @cancel="showDeleteVideoModal = false"
      @confirm="deleteVideo()"
      title="Deleting Video"
      message="Are you sure you want to delete the video?"
      :show="showDeleteVideoModal"/>

    <Confirm
      @cancel="showRemoveVideoModal = false"
      @confirm="removeVideo()"
      title="Remove Video From My List"
      message="Are you sure you want to remove this shared video from your list?"
      :show="showRemoveVideoModal"
      :completeButtonText="'Remove'"/>

    <TransitionFade>
    <ShareModal
      :video ="video"
      v-if="isOwner || isCoachWhoCanShare"
      v-show="showShareModal"
      @close="closeShareModal"/>
    </TransitionFade>

    <MovetoLibraryModal
      :videoID="videoID"
      @close="showMoveToLibraryModal = false"
      v-if="showMoveToLibraryModal && isOwner"/>

    <MoveToFolderModal
      :video="video"
      @close="showMoveToFolderModal = false"
      v-show="showMoveToFolderModal && isOwner"/>
  </div>
</template>

<script>
import BasePage from './BasePage.vue'
import VideoPlayer from './VideoPlayer.vue'
import Loader from './Loader.vue'
import Comments from './Comments.vue'
import Confirm from './Confirm.vue'
import Resource from './Resource.vue'
import AddFileModal from './AddFileModal.vue'
import ShareModal from './ShareModal.vue'
import MovetoLibraryModal from './MovetoLibraryModal.vue'
import MoveToFolderModal from './MoveToFolderModal.vue'
import FolderIcon from './FolderIcon.vue'
import TransitionFade from './TransitionFade'
import Dropdown from './Dropdown'
import ArtsFormComments from './ArtsFormComments.vue'

import vueSlider from './vue-slider.vue'

import store from '../store.js'

const arraysEqual = (a, b) =>
a.length === b.length && a.every((x, i) => x === b[i])

import { textautosize } from '../directives.js'

import { prettyCommentTime, formatTextDate } from '../filters.js'

const videoElement = () => document.querySelector('video')

export default {
  name: 'VideoBase',
  components: {
    BasePage,
    vueSlider,
    VideoPlayer,
    Loader,
    Comments,
    Confirm,
    AddFileModal,
    Resource,
    ShareModal,
    MovetoLibraryModal,
    MoveToFolderModal,
    FolderIcon,
    TransitionFade,
    Dropdown,
    ArtsFormComments,
  },
  directives: {
    textautosize,
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      showDeleteVideoModal: false,
      showRemoveVideoModal: false,
      showMoveToLibraryModal: false,
      showMoveToFolderModal: false,
      description: '',
      singleVideo: true,
      editing: false,
      duration: 0,
      currentTime: 0,
      slider: {
        value: [0, 500],
        min: 0,
        max: 500,
        disabled: false,
        show: true,
      },
      narrowVideoMode: false,
    }
  },
  watch: {
    volume (val) {
      const video = videoElement()
      video.volume = val
      // if we have a muted volume, we should actually mute the <video>
      if (video.volume > 0) {
        this.muted = false
      }
    },
    muted (val) {
      const video = videoElement()
      video.muted = val
      // if the video is muted, we turn the volume to zero to better indicate
      // that the video is muted
      if (video.muted) {
        this.volumeOld = this.volume
        this.volume = 0
      } else {
        this.volume = this.volumeOld
      }
    },
    editing (val) {
      console.log('editing updated')
      if (val) {
        this.description = this.video.description
      }
    },
  },
  mounted () {
    this.setUp()
    store.dispatch('fetchFolders')
    store.dispatch('fetchGroup')
  },
  computed: {
    videoID () {
      return this.video.id
    },
    isOwner () {
      return this.video && this.video.user.id === store.state.user.id
    },
    isCoachWhoCanShare () {
      return store.state.user.is_coach && store.state.group.coaches_can_share
    },
    isGroupAdmin () {
      return store.state.user.is_group_admin
    },
    clippable () {
      return !arraysEqual(this.slider.value, [0, 500])
    },
    trimmable () {
      return this.video.trimmable
    },
    downloadable () {
      return store.state.user.downloads_enabled
    },
    downloadOverride () {
      return store.state.group.id === 127 || store.state.user.id === 631 || store.state.user.id === 633
    },
    showAddFileModal () {
      return store.state.showAddFileModal
    },
    showAddVideoModal () {
      return store.state.showAddVideoModal
    },
    showShareModal () {
      return store.state.showShareModal
    },
    clippingVideo () {
      return store.state.clippingVideo
    },
    sliderStartTime () {
      return this.slider.value[0] / this.slider.max * this.duration
    },
    sliderEndTime () {
      return this.slider.value[1] / this.slider.max * this.duration
    },
    resources () {
      return this.video.resources
    },
    folders () {
      return store.state.folders
    },
    currentFolder () {
      return this.folders.find(f => f.id === this.video.folder)
    },
    obsButtonLabel () {
      return (this.narrowVideoMode ? 'Return to Video Mode' : 'Launch Observation Mode')
    },
    hasObservationsPerm () {
      return store.state.user.perm_observations
    },
  },
  methods: {
    handleSliderChange (value) {
      this.slider.value = value
      const video = videoElement()
      video.currentTime = this.sliderStartTime
    },
    updateCurrentTime (time) {
      this.currentTime = time
    },
    toggleShowAddFileModal () {
      store.commit('toggleShowAddFileModal')
    },
    toggleShowShareModal () {
      store.commit('toggleShowShareModal')
    },
    closeShareModal () {
      if (this.showShareModal) {
        store.commit('closeShareModal')
      }
    },
    clipperTimeStampFormatter (value) {
      const percentageOfTime = value / this.slider.max * this.duration
      return prettyCommentTime(percentageOfTime)
    },
    async clipVideo ({ replace }) {
      const videoID = this.videoID
      const startTime = this.sliderStartTime
      const endTime = this.sliderEndTime
      await store.dispatch('clipVideo', {
        videoID,
        startTime,
        endTime,
        replace,
      })
      this.editing = false
      this.$router.push({ path: '/' })
    },
    toggleShowAddVideoModal () {
      store.commit('setShowAddVideoModal', !this.showAddVideoModal)
    },
    handlePause () {
      videoElement().pause()
    },
    async updateVideo (videoID) {
      const title = this.$refs.title.value
      const description = this.description
      const cycleID = null
      await store.dispatch('updateVideo', { cycleID, videoID, title, description })
      this.editing = false
    },
    async deleteVideo () {
      const { videoID } = this

      await store.dispatch('deleteVideoByID', { videoID })

      this.editing = false
      this.showDeleteVideoModal = false
      this.$router.push({ path: '/' })
    },
    async removeVideo () {
      const { videoID } = this

      await store.dispatch('removeVideoByID', { videoID })

      this.editing = false
      this.showRemoveVideoModal = false
      this.$router.push({ path: '/' })
    },
    async updateCommenting (event, videoID) {
      const commenting = event.target.value
      await store.dispatch('updateVideoCommenting', { videoID, commenting })
    },
    setUp () {
      // disable editing & reset slider to initial values
      this.editing = false
      this.slider.value = [0, 500]

      clearInterval(this.interval)

      store.commit('setVideoPlayerSource', this.video.url)
      this.createInterval()
    },
    // create a checker that periodically checks if the video has processed yet
    createInterval (seconds = 10) {
      console.log('creating interval')
      const sec = 1000
      const interval = seconds * sec

      // then we create another interval for the new video

      // TODO: if we refactor out the video play & video description / clip
      // editor into its own view with a nested router, this could be
      // accomplished in a mounted() method
      this.interval = setInterval(() => {
        const videoID = this.videoID
        if (this.video == null || videoID === -1) return

        if (this.video.processed) {
          console.log('cleared interval')
          clearInterval(this.interval)
          return
        }
        const cycleID = null
        store.dispatch('checkVideoProcessed', { cycleID, videoID })
      }, interval)
    },
  },
  filters: {
    prettyCommentTime,
    formatTextDate,
    shortDate (d) {
      d = new Date(d)
      if (d == null) return ''
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept',
        'Oct', 'Nov', 'Dec',
      ]

      const currentYear = new Date().getFullYear()
      const day = d.getDate()
      const monthName = months[d.getMonth()]
      const output = monthName + ' ' + day
      const year = d.getFullYear()
      if (year !== currentYear) {
        return output + ', ' + year
      }
      return output
    },
  },
}
</script>

<style lang="scss">
@import '../video-container.scss';
@import '~bulma/sass/utilities/mixins.sass';

.tarp {
  z-index: 10;
  position: relative;
  background-color: white;
}

.video-placeholder-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 450px;
  flex-direction: column;
}

.flex-end {
  align-items: flex-end;
}

.main-container {
  background-color: #eaeaea;
}

.li-bottom-border {
  border-bottom: 1px solid #dbdbdb;
}

.filename-edit-input {
  width: 75%;
}


.tag {
  margin: 0px 2px;
  border-radius: 6px;
}

button.white-btn-text {
  color: white;
}

button.is-warning.white-btn-text:hover {
  color: white;
  background-color: #f9aa55;
}

.header-container {
  align-items: center;
  display: flex;
  margin-top: 5px;
  margin-bottom: 5px;
}

.title.no-margin-bottom,
.no-margin-bottom {
  margin-bottom: 0;
}

label.margin-right {
  margin-right: 10px;
}

.editing-container {
  display: flex;

  & > .button {
    margin-right: 10px;
  }
  & > .button:last-child {
    margin-right: 0;
  }
}

.video-placeholder-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.start-end-container {
  margin-top: 10px;
  margin-bottom: 10px;
}

.select-container {
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem;
}

.title-input {
  width: 100%;
}

.title-container {
  flex-grow: 1;
  margin-right: 1rem;
}

.is-thick {
  font-weight: 500;
}

.is-bold {
  font-weight: bold;
}

@import '../_variables.scss';

.no-pd-right-desktop {

  @include desktop {
    padding-right: 0 !important;
  }
}


.icon {
  width: 20px;
  height: 20px;
  fill: initial;
  &.is-active {
    fill: $teal;
  }
}


.flex-end {
  align-items: flex-end;
}

.main-container {
  background-color: #eaeaea;
}

.li-bottom-border {
  border-bottom: 1px solid #dbdbdb;
}

.filename-edit-input {
  width: 75%;
}

.video-title {
  max-width: 1000px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 500;
  flex-wrap: nowrap;

  @include mobile {
    font-size: 1.2rem;
  }

  @include tablet {
    font-size: 1.5rem;
  }
}

.background-light {
  background: hsla(0, 0%, 97%, 1);
}

.box-shadow {
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
}

.br-top-0 {
  border-radius-top-right-radius: 0;
  border-radius-top-left-radius: 0;
}

.br-bottom-0 {
  border-radius-bottom-right-radius: 0;
  border-radius-bottom-left-radius: 0;
}

</style>

<style lang="scss" scoped>

img.user-profile-image {
  height: 60px;
  width: 60px;
}

.bottom-border {
  padding-bottom: 10px;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

</style>



// WEBPACK FOOTER //
// VideoBase.vue?4290a476